<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100 mb-4">
        Location Links
      </h3>

      <!-- GBP Location Links -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">GBP Location Link(s)</h4>
        <div class="space-y-2">
          <div v-for="(link, idx) in gbpLinks" :key="'gbp-' + idx" class="flex items-center gap-2">
            <div class="flex-1">
              <UiInput v-model="gbpLinks[idx]" placeholder="https://business.google.com/..." />
            </div>
            <button
              v-if="gbpLinks.length > 1"
              type="button"
              class="p-2 text-gray-400 hover:text-red-500 transition-colors"
              @click="removeItem(gbpLinks, idx)"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            class="text-sm text-primary-500 hover:text-primary-600 font-medium"
            @click="addItem(gbpLinks)"
          >
            + Add another GBP link
          </button>
        </div>
      </div>

      <!-- Website Location URLs -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Website Location URL(s)</h4>
        <div class="space-y-2">
          <div v-for="(link, idx) in websiteUrls" :key="'web-' + idx" class="flex items-center gap-2">
            <div class="flex-1">
              <UiInput v-model="websiteUrls[idx]" placeholder="https://example.com/locations/..." />
            </div>
            <button
              v-if="websiteUrls.length > 1"
              type="button"
              class="p-2 text-gray-400 hover:text-red-500 transition-colors"
              @click="removeItem(websiteUrls, idx)"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            class="text-sm text-primary-500 hover:text-primary-600 font-medium"
            @click="addItem(websiteUrls)"
          >
            + Add another website URL
          </button>
        </div>
      </div>

      <!-- Local Viking Links -->
      <div>
        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Local Viking Link(s)</h4>
        <div class="space-y-2">
          <div v-for="(link, idx) in vikingLinks" :key="'viking-' + idx" class="flex items-center gap-2">
            <div class="flex-1">
              <UiInput v-model="vikingLinks[idx]" placeholder="https://localviking.com/..." />
            </div>
            <button
              v-if="vikingLinks.length > 1"
              type="button"
              class="p-2 text-gray-400 hover:text-red-500 transition-colors"
              @click="removeItem(vikingLinks, idx)"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            class="text-sm text-primary-500 hover:text-primary-600 font-medium"
            @click="addItem(vikingLinks)"
          >
            + Add another Local Viking link
          </button>
        </div>
      </div>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100 mb-4">
        Citations & PR Contact
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiControl id="seo-locations-citation-phone" label="Phone for Citations & PRs">
          <UiInput v-model="form.citation_phone" placeholder="(555) 123-4567" />
        </UiControl>
        <UiControl id="seo-locations-citation-email" label="Email for Citations & PRs">
          <UiInput v-model="form.citation_email" type="email" placeholder="email@example.com" />
        </UiControl>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import UiInput from '~/javascript/components/ui/form/input/input.vue'

const props = defineProps({
  stepKey: { type: String, required: true },
  label: { type: String, required: true },
  onboarding: { type: Object, required: true },
  mode: { type: String, required: true }
})

const gbpLinks = reactive([''])
const websiteUrls = reactive([''])
const vikingLinks = reactive([''])

const form = ref({
  citation_phone: '',
  citation_email: ''
})

function addItem(arr) {
  arr.push('')
}

function removeItem(arr, idx) {
  arr.splice(idx, 1)
}

function initList(arr, source) {
  arr.length = 0
  if (source && source.length > 0) {
    source.forEach(item => arr.push(item))
  } else {
    arr.push('')
  }
}

onMounted(() => {
  const d = props.onboarding.seo_detail || {}
  initList(gbpLinks, d.gbp_location_links)
  initList(websiteUrls, d.website_location_urls)
  initList(vikingLinks, d.local_viking_links)
  form.value.citation_phone = d.citation_phone || ''
  form.value.citation_email = d.citation_email || ''
})

defineExpose({
  getData: () => ({
    gbp_location_links: gbpLinks.filter(l => l.trim() !== ''),
    website_location_urls: websiteUrls.filter(l => l.trim() !== ''),
    local_viking_links: vikingLinks.filter(l => l.trim() !== ''),
    ...form.value
  })
})
</script>
